import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { MessageButton } from "@/components/messaging/MessageButton";
import { ProductGrid } from "@/components/products/ProductGrid";
import { SupplierReviews } from "@/components/suppliers/SupplierReviews";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getSupplierTags, SupplierTag } from "@/hooks/use-supplier-tags";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { isProduction } from "@/utils/environment";
import {
	AlertTriangle,
	ArrowLeft,
	Building2,
	Clock,
	Globe,
	MapPin,
	Store,
	Truck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

interface SupplierProfile {
	id: string;
	name: string | null;
	business_name: string | null;
	account_type: string;
	location?: string | null;
	description?: string | null;
	profile_image?: string | null;
	minimum_free_shipping?: number | null;
	shipping_cost: number;
	minimum_order: number;
	min_delivery_days?: number | null;
	max_delivery_days?: number | null;
	is_test?: boolean | null;
	website?: string | null;
	year_established?: number | null;
}

interface Product {
	id: string;
	name: string;
	description: string | null;
	price: number;
	category: string | null;
	msrp: number | null;
	sample_available: boolean;
	sample_quantity: number;
	sample_cost: number;
	deleted?: boolean;
	images: { id: string; image_url: string; is_primary: boolean | null }[];
}

const SupplierProfile = () => {
	const { id } = useParams<{ id: string }>();
	const [supplier, setSupplier] = useState<SupplierProfile | null>(null);
	const [products, setProducts] = useState<Product[]>([]);
	const [loading, setLoading] = useState(true);
	const [supplierTags, setSupplierTags] = useState<SupplierTag[]>([]);
	const navigate = useNavigate();
	const isProd = isProduction();

	useEffect(() => {
		if (id) {
			fetchSupplier(id);
		}
	}, [id]);

	useEffect(() => {
		if (supplier) {
			const isTestSupplier = supplier.is_test === true;
			if ((isProd && isTestSupplier) || (!isProd && !isTestSupplier)) {
				navigate("/products");
				return;
			}

			fetchSupplierProducts(supplier.id);
		}
	}, [supplier, isProd, navigate]);

	useEffect(() => {
		if (supplier) {
			fetchSupplierTags(supplier.id);
		}
	}, [supplier]);

	const fetchSupplier = async (supplierId: string) => {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", supplierId)
				.single();

			if (error) throw error;
			console.log("data", data);
			setSupplier(data);
		} catch (error) {
			console.error("Error fetching supplier:", error);
		}
	};

	const fetchSupplierProducts = async (supplierId: string) => {
		try {
			setLoading(true);

			const { data: productsData, error: productsError } = await supabase
				.from("products")
				.select("*")
				.eq("supplier_id", supplierId)
				.eq("deleted", false)
				.order("created_at", { ascending: false });

			if (productsError) throw productsError;

			const productIds = productsData.map((product) => product.id);

			if (productIds.length > 0) {
				const { data: imagesData, error: imagesError } = await supabase
					.from("product_images")
					.select("*")
					.in("product_id", productIds);

				if (imagesError) throw imagesError;

				const productsWithImages = productsData.map((product) => ({
					...product,
					category: product.category_id || null,
					images:
						imagesData.filter(
							(img) => img.product_id === product.id
						) || [],
				}));

				setProducts(productsWithImages);
			} else {
				setProducts(
					productsData.map((product) => ({
						...product,
						category: product.category_id || null,
						images: [],
					}))
				);
			}
		} catch (error) {
			console.error("Error fetching supplier products:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchSupplierTags = async (supplierId: string) => {
		try {
			const tags = await getSupplierTags(supplierId);
			console.log("tags", tags);
			setSupplierTags(tags);
		} catch (error) {
			console.error("Error fetching supplier tags:", error);
		}
	};

	const getPrimaryImageUrl = (product: Product) => {
		const primaryImage = product.images.find((img) => img.is_primary);
		return (
			primaryImage?.image_url ||
			product.images[0]?.image_url ||
			"/placeholder.svg"
		);
	};

	const formattedProducts = products.map((product) => ({
		id: product.id,
		name: product.name,
		brand: supplier?.business_name || supplier?.name || "Unknown Supplier",
		description: product.description || "",
		price: product.price,
		minOrder: 1,
		image: getPrimaryImageUrl(product),
		category: product.category || "",
		tags: [],
		supplierId: id,
		shippingCost: supplier?.shipping_cost || 0,
		msrp: product.msrp || undefined,
		caseQuantity: 1,
		sampleAvailable: product.sample_available || false,
		sampleQuantity: product.sample_quantity || 1,
		sampleCost: product.sample_cost || 0,
		isTestSupplier: supplier?.is_test || false,
	}));

	if (loading && !supplier) {
		return (
			<div className="min-h-screen flex flex-col">
				<Navbar />
				<main className="flex-grow pt-20">
					<div className="container max-w-6xl mx-auto px-6 md:px-10 py-10">
						<div className="flex flex-col md:flex-row gap-8">
							<Skeleton className="h-40 w-full md:w-1/3 rounded-lg" />
							<div className="w-full md:w-2/3 space-y-4">
								<Skeleton className="h-10 w-2/3" />
								<Skeleton className="h-6 w-1/3" />
								<Skeleton className="h-24 w-full" />
							</div>
						</div>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	if (!loading && !supplier) {
		return (
			<div className="min-h-screen flex flex-col">
				<Navbar />
				<main className="flex-grow pt-20">
					<div className="container max-w-6xl mx-auto px-6 md:px-10 py-20 text-center">
						<h1 className="text-3xl font-display font-semibold mb-4">
							Supplier Not Found
						</h1>
						<p className="text-gray-600 mb-8">
							The supplier you're looking for doesn't exist or has
							been removed.
						</p>
						<Link to="/products">
							<Button className="gap-2">
								<ArrowLeft className="h-4 w-4" />
								Back to Products
							</Button>
						</Link>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col">
			<Navbar />
			<main className="flex-grow pt-20">
				<div className="container max-w-6xl mx-auto px-6 md:px-10 py-10">
					<div className="flex text-sm text-gray-500 mb-8 items-center">
						<Link to="/" className="hover:text-primary">
							Home
						</Link>
						<span className="mx-2">/</span>
						<Link to="/products" className="hover:text-primary">
							Products
						</Link>
						<span className="mx-2">/</span>
						<span className="text-gray-700 font-medium truncate">
							{supplier?.business_name || supplier?.name}
						</span>
					</div>

					<div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 md:p-8 mb-12">
						<div className="flex flex-col md:flex-row gap-8 items-center">
							<div className="md:w-1/4 flex flex-col items-center">
								<Avatar className="h-55 w-55 mb-7">
									<AvatarImage
										src={
											supplier?.profile_image || undefined
										}
										alt={
											supplier?.business_name ||
											supplier?.name ||
											""
										}
									/>
									<AvatarFallback className="text-3xl bg-primary/10 text-primary">
										{(
											supplier?.business_name ||
											supplier?.name ||
											""
										)
											.split(" ")
											.map((n) => n[0])
											.join("")
											.toUpperCase()
											.slice(0, 2)}
									</AvatarFallback>
								</Avatar>

								{supplier?.year_established && (
									<div className="my-1 flex items-center gap-1 text-gray-500 text-sm">
										<Building2 className="h-4 w-4" />
										<span>
											Established{" "}
											{supplier.year_established}
										</span>
									</div>
								)}

								{supplier?.location && (
									<div className="my-1 flex items-center gap-1 text-gray-500 text-sm">
										<MapPin className="h-4 w-4" />
										<span>{supplier.location}</span>
									</div>
								)}

								{supplier?.is_test && (
									<div className="my-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
										Test Supplier
									</div>
								)}

								{supplier?.website && (
									<a
										href={
											supplier.website.startsWith("http")
												? supplier.website
												: `https://${supplier.website}`
										}
										target="_blank"
										rel="noopener noreferrer"
										className="my-1 text-gray-500 text-sm flex items-center gap-1 hover:text-primary transition-colors">
										<Globe className="h-3.5 w-3.5" />
										{supplier.website}
									</a>
								)}
							</div>

							<div className="md:w-3/4">
								<div className="flex items-center gap-2 mb-4">
									<h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900">
										{supplier?.business_name ||
											supplier?.name}
									</h1>
								</div>

								<div className="space-y-5">
									{supplier?.description && (
										<div className="rounded-lg">
											<div className="flex-1">
												<p className="text-gray-600">
													{supplier.description}
												</p>
											</div>
										</div>
									)}

									<div className="space-y-4 ml-1 text-sm">
										{supplier?.min_delivery_days !== null &&
											supplier?.max_delivery_days !==
												null && (
												<div className="flex items-center gap-2">
													<Clock className="h-5 w-5 text-gray-500 shrink-0" />
													<span className="text-gray-700">
														{
															supplier.min_delivery_days
														}
														-
														{
															supplier.max_delivery_days
														}{" "}
														days delivery
													</span>
												</div>
											)}

										{supplier.minimum_free_shipping !==
											null && (
											<div className="flex items-center gap-2">
												<Truck className="h-5 w-5 text-gray-500 shrink-0" />
												<span className="text-gray-700">
													Free shipping over{" "}
													{formatCurrency(
														supplier.minimum_free_shipping
													)}
												</span>
											</div>
										)}

										{supplier?.minimum_order > 0 && (
											<div className="flex items-center gap-2">
												<AlertTriangle className="h-5 w-5 text-gray-500 shrink-0" />
												<span className="text-gray-700">
													Min. order:{" "}
													{formatCurrency(
														supplier.minimum_order
													)}
												</span>
											</div>
										)}
									</div>

									<div className="flex flex-wrap gap-3 my-4">
										<MessageButton
											supplierId={supplier?.id || ""}
											supplierName={
												supplier?.business_name ||
												supplier?.name ||
												""
											}
										/>
									</div>
									{supplierTags.length > 0 && (
										<div className="flex flex-wrap gap-2 mt-6">
											{supplierTags.map((tag) => (
												<Badge
													key={tag.id}
													variant="secondary">
													{tag.name}
												</Badge>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					<div>
						<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-8">
							Products from this Supplier
						</h2>

						{formattedProducts.length > 0 ? (
							<ProductGrid products={formattedProducts} />
						) : (
							<div className="text-center py-10 border border-dashed rounded-lg">
								<Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
								<h3 className="text-lg font-medium mb-2">
									No products available
								</h3>
								<p className="text-gray-500">
									This supplier has no products listed yet.
								</p>
							</div>
						)}
					</div>
					<div className="mt-8">
						<SupplierReviews supplierId={id || ""} />
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
};

export default SupplierProfile;
